(() => {
  'use strict';

  const $$ = (root, selector) => [...(root?.querySelectorAll(selector) || [])];
  const $ = (root, selector) => root?.querySelector(selector) || null;
  const text = (value) => String(value ?? '').trim();
  const norm = (value) => text(value).toLowerCase().replace(/[^a-z0-9]+/g, '');
  const split = (value) => text(value).split('||').map(text).filter(Boolean);
  const itemKey = (item) => String(item?.key ?? item?.id ?? item?.value ?? '');
  const itemLabel = (item) => text(item?.display ?? item?.label ?? item?.name ?? item?.title ?? itemKey(item));
  const items = (value) => {
    if (Array.isArray(value)) {
      return value.map((item, index) => typeof item === 'string'
        ? { key: norm(item) || String(index), label: item }
        : { key: String(item?.key ?? item?.id ?? item?.value ?? index), ...(item || {}) });
    }
    if (value && typeof value === 'object') {
      return Object.entries(value).map(([key, item]) => typeof item === 'string'
        ? { key, label: item }
        : { key, ...(item || {}) });
    }
    return [];
  };
  const firstUrl = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return /^(https?:)?\/\//i.test(value) ? value : '';
    if (Array.isArray(value)) {
      for (const item of value) {
        const url = firstUrl(item);
        if (url) return url;
      }
    } else if (typeof value === 'object') {
      for (const key of ['base', 'image', 'url', 'src', 'thumbnail']) {
        const url = firstUrl(value[key]);
        if (url) return url;
      }
      for (const item of Object.values(value)) {
        const url = firstUrl(item);
        if (url) return url;
      }
    }
    return '';
  };
  const swatch = (item, index) => item?.tone || item?.swatch || item?.hex || item?.color
    || (Array.isArray(item?.swatches) && item.swatches.length ? `linear-gradient(135deg,${item.swatches.join(',')})` : '')
    || ['#15191e', '#747b84', '#f2f4f6', '#d5d9dd', '#64c9d8', '#e7a2b4'][index % 6];

  function init(root) {
    if (!root || root.dataset.msqReady === '1') return;
    root.dataset.msqReady = '1';
    root.dataset.msqVersion = '12.4-native-stable';

    let config = {};
    try {
      /* Phase 19.2: the configurator JSON is emitted once on the page
         (data-lite-config, product overview). Fall back to it when this
         section has no local copy; standalone usage degrades to '{}'
         (section fallback colors/visors) as before. */
      config = JSON.parse($(root, '[data-msq-config]')?.textContent || document.querySelector('[data-lite-config]')?.textContent || '{}');
      if (typeof config === 'string') config = JSON.parse(config);
    } catch (error) {
      console.error('MASONTEX OEM config error', error);
    }

    let i18n = {};
    try {
      i18n = JSON.parse($(root, '[data-msq-i18n]')?.textContent || '{}');
    } catch (error) {
      console.error('MASONTEX OEM i18n error', error);
    }

    const labels = i18n.labels || {};
    const translatedValues = i18n.values || {};
    const displayLabel = (rawValue) => labels[text(rawValue).toUpperCase()] || text(rawValue);
    const displayValue = (rawValue) => {
      const raw = text(rawValue);
      if (!raw) return raw;
      const key = raw.toUpperCase();
      const exact = {
        'ADVANCED OEM CUSTOM': i18n.advancedVersion,
        'STANDARD VERSION': i18n.standardVersion,
        'GRAPHIC CUSTOMIZATION': i18n.graphicCustomization,
        'SOLID COLOR': i18n.solidColor,
        'TO BE CONFIRMED': i18n.willConfirm,
        'WILL CONFIRM LATER': i18n.willConfirm,
        'NOT UPLOADED': i18n.notUploaded,
        'NO FILE UPLOADED': i18n.notUploaded,
        'NOT INCLUDED': i18n.notIncluded,
        'NOT APPLICABLE': i18n.notApplicable,
        'STANDARD CONFIGURATION': i18n.standardConfiguration,
        'NO CUSTOMIZATION': i18n.noCustomization,
        'CUSTOMER LOGO': i18n.customerLogo,
        'HELMET DUST BAG': i18n.helmetDustBag,
        '3-LAYER CARTON': i18n.carton
      };
      if (exact[key]) return exact[key];
      if (translatedValues[key]) return translatedValues[key];
      if (raw.includes('/')) {
        const parts = raw.split(/\s*\/\s*/);
        const mapped = parts.map((part) => translatedValues[part.toUpperCase()] || exact[part.toUpperCase()] || part);
        if (mapped.some((part, index) => part !== parts[index])) return mapped.join(' / ');
      }
      const pcs = raw.match(/^(\d+(?:\.\d+)?)\s*PCS$/i);
      if (pcs) return `${pcs[1]} ${i18n.pcs || 'PCS'}`;
      return raw;
    };

    let colors = items(config.colors);
    let visors = items(config.visors);
    if (!colors.length) colors = split(root.dataset.fallbackColors).map((label, index) => ({ key: norm(label) || `c${index}`, label }));
    if (!visors.length) visors = split(root.dataset.fallbackVisors).map((label, index) => ({ key: norm(label) || `v${index}`, label }));

    const graphics = [
      { key: 'Custom Artwork / Decals', label: 'Custom Artwork / Decals' },
      { key: 'Partial Graphic Design', label: 'Partial Graphic Design' },
      { key: 'Full Helmet Graphic Design', label: 'Full Helmet Graphic Design' }
    ];
    const liners = [
      { key: 'Standard Liner', label: 'Standard Liner' },
      { key: 'Custom Color / Branding', label: 'Custom Color / Branding' }
    ];
    const packagingOptions = [
      { key: 'Protective Bag + Individual Inner Box', label: 'Protective Bag + Individual Inner Box' },
      { key: 'Custom Color Box', label: 'Custom Color Box' },
      { key: 'Custom Labels / Manual', label: 'Custom Labels / Manual' }
    ];

    const moq = Math.max(1, Number(root.dataset.standardMoq || 100));
    const state = {
      tier: 'standard',
      step: 'color',
      customType: 'solid',
      color: itemKey(colors[0]),
      customColor: '',
      finish: 'Gloss',
      graphicType: graphics[0].key,
      graphicNotes: '',
      logoMode: 'Customer Logo',
      logoPosition: 'Side',
      logoFile: null,
      visor: itemKey(visors[0]),
      material: 'PC Reinforced',
      quantity: Math.max(moq, Number(root.dataset.defaultQuantity || moq)),
      liner: liners[0].key,
      packaging: packagingOptions[0].key,
      market: '',
      notes: '',
      standardBag: true,
      standardBox: true
    };

    const summaryImage = $(root, '[data-msq-image]');
    const originalImage = summaryImage?.getAttribute('src') || '';
    const modal = document.getElementById('V12OemRequest');
    const form = modal?.querySelector('form.ms-unified-inquiry__form, form.msq-inquiry__form, form');
    const modalQuantity = modal?.querySelector('[data-msq-modal-quantity]');

    const colorItem = () => colors.find((item) => itemKey(item) === state.color) || colors[0] || {};
    const visorItem = () => visors.find((item) => itemKey(item) === state.visor) || visors[0] || {};
    const rawColorName = () => itemLabel(colorItem()) || 'Current Finish';
    const rawVisorName = () => itemLabel(visorItem()) || 'Clear';
    const displayColorName = () => displayValue(rawColorName());
    const displayVisorName = () => displayValue(rawVisorName());
    const colorImage = () => firstUrl(colorItem()) || firstUrl(config?.structures?.closed?.colors?.[state.color]) || originalImage;

    const setText = (selector, value) => {
      const node = $(root, selector);
      if (node) node.textContent = text(value) || '—';
    };
    const setModalText = (selector, value) => {
      const node = $(modal, selector);
      if (node) node.textContent = text(value) || '—';
    };
    const setHidden = (selector, hidden) => {
      $$(root, selector).forEach((node) => { if (node.hidden !== hidden) node.hidden = hidden; });
    };
  const ensureFormField = (key, name) => {
    if (!form) return null;
    return $(form, `[data-msq-contact="${key}"]`) || null;
  };
    const put = (key, name, value) => {
      const field = ensureFormField(key, name);
      if (field) field.value = text(value);
    };
    const removeLegacyFields = () => {
      if (!form) return;
      ['certification', 'graphics'].forEach((key) => $(form, `[data-msq-contact="${key}"]`)?.remove());
    };

    function syncForm() {
      if (!form) return;
      removeLegacyFields();
      const advanced = state.tier === 'advanced';
      const solid = advanced && state.customType === 'solid';
      const graphic = advanced && state.customType === 'graphic';
      const quantity = advanced ? Math.max(moq, state.quantity) : moq;
      const standardPackage = [state.standardBag ? 'Helmet Dust Bag' : '', state.standardBox ? 'Standard 3-Layer Carton' : ''].filter(Boolean).join(' + ') || 'No standard package selected';
      const packaging = advanced ? state.packaging : standardPackage;

      put('version', 'contact[Order version]', advanced ? 'Advanced OEM Custom' : 'Standard Version');
      put('product', 'contact[Product / Model]', `${root.dataset.productTitle || ''} / ${root.dataset.model || ''}`);
      put('customType', 'contact[Customization type]', advanced ? (graphic ? 'Graphic Customization' : 'Solid Color') : 'Standard Version');
      put('color', 'contact[Helmet color]', rawColorName());
      put('customColor', 'contact[Custom color / Pantone]', solid ? (state.customColor || 'To be confirmed') : 'Not applicable');
      put('finish', 'contact[Surface finish]', solid ? state.finish : 'Not applicable');
      put('graphicType', 'contact[Graphic customization type]', graphic ? state.graphicType : 'Not applicable');
      put('graphicNotes', 'contact[Graphic description]', graphic ? (state.graphicNotes || 'To be confirmed') : 'Not applicable');
      put('visor', 'contact[Visor color]', advanced ? rawVisorName() : 'Standard configuration');
      put('material', 'contact[Visor material]', advanced ? state.material : 'Standard configuration');
      put('logo', 'contact[Logo]', advanced ? state.logoMode : 'No customization');
      put('logoPosition', 'contact[Logo position]', advanced ? state.logoPosition : 'Not applicable');
      put('logoFile', 'contact[Logo file]', advanced ? (state.logoFile?.name || 'Not uploaded') : 'Not applicable');
      put('quantity', 'contact[Quantity]', `${quantity}`);
      put('packaging', 'contact[Packaging]', packaging);
      put('standardBag', 'contact[Helmet dust bag]', advanced ? 'Not applicable' : (state.standardBag ? 'Included' : 'Not included'));
      put('standardBox', 'contact[Standard 3-layer carton]', advanced ? 'Not applicable' : (state.standardBox ? 'Included' : 'Not included'));
      put('liner', 'contact[Inner liner]', advanced ? state.liner : 'Standard');
      put('market', 'contact[Target market]', advanced ? (state.market || 'To be confirmed') : 'To be confirmed');
      put('url', 'contact[Product page]', root.dataset.productUrl || location.href);

      if (modalQuantity && document.activeElement !== modalQuantity) {
        modalQuantity.value = String(quantity);
        modalQuantity.min = String(moq);
      }
    }

    function buildChoices(containers, type, data) {
      const targets = Array.isArray(containers) ? containers : [containers].filter(Boolean);
      targets.forEach((container) => {
        container.innerHTML = '';
        data.forEach((item, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'ms-quick__choice';
          button.dataset.msqChoice = type;
          button.dataset.key = itemKey(item);
          const dot = document.createElement('i');
          dot.style.background = swatch(item, index);
          const strong = document.createElement('strong');
          strong.textContent = displayValue(itemLabel(item));
          button.append(dot, strong);
          button.addEventListener('click', () => {
            state[type] = itemKey(item);
            render();
          });
          container.append(button);
        });
      });
    }

    buildChoices($(root, '[data-msq-colors]'), 'color', colors);
    buildChoices($(root, '[data-msq-visors]'), 'visor', visors);
    buildChoices($$(root, '[data-msq-graphics]'), 'graphicType', graphics);
    buildChoices($$(root, '[data-msq-liners]'), 'liner', liners);
    buildChoices($$(root, '[data-msq-packaging]'), 'packaging', packagingOptions);

    function renderPanels() {
      const advanced = state.tier === 'advanced';
      const visibleStep = state.step || 'color';
      $$(root, '[data-msq-oem-step]').forEach((button) => {
        const active = button.dataset.msqOemStep === visibleStep;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-current', active ? 'step' : 'false');
      });
      $$(root, '[data-msq-oem-panel]').forEach((panel) => {
        panel.hidden = !advanced || panel.dataset.msqOemPanel !== visibleStep;
      });
    }

    function syncModalPreview() {
      const advanced = state.tier === 'advanced';
      setModalText('[data-msq-modal-model]', root.dataset.model || root.dataset.productTitle || 'HELMET');
      setModalText('[data-msq-summary="version"]', advanced ? (i18n.advancedVersion || 'ADVANCED OEM CUSTOM') : (i18n.standardVersion || 'STANDARD VERSION'));
      setModalText('[data-msq-summary="color"]', displayColorName());
      setModalText('[data-msq-summary="visor"]', displayVisorName());
      setModalText('[data-msq-summary="quantity"]', `${advanced ? Math.max(moq, state.quantity) : moq} ${i18n.pcs || 'PCS'}`);
    }

    function render() {
      const advanced = state.tier === 'advanced';
      const solid = advanced && state.customType === 'solid';
      const graphic = advanced && state.customType === 'graphic';

      $$(root, '[data-msq-tier]').forEach((button) => {
        const active = button.dataset.msqTier === state.tier;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      $$(root, '[data-msq-choice]').forEach((button) => button.classList.toggle('is-active', state[button.dataset.msqChoice] === button.dataset.key));
      $$(root, '[data-msq-custom-type-solid]').forEach((button) => {
        button.classList.toggle('is-active', state.customType === 'solid');
        button.setAttribute('aria-pressed', String(state.customType === 'solid'));
      });
      $$(root, '[data-msq-custom-type-graphic]').forEach((button) => {
        button.classList.toggle('is-active', state.customType === 'graphic');
        button.setAttribute('aria-pressed', String(state.customType === 'graphic'));
      });
      $$(root, '[data-msq-finish]').forEach((button) => button.classList.toggle('is-active', text(button.dataset.msqValue).toLowerCase() === text(state.finish).toLowerCase()));
      $$(root, '[data-msq-logo-position]').forEach((button) => button.classList.toggle('is-active', text(button.dataset.msqValue).toLowerCase() === text(state.logoPosition).toLowerCase()));
      $$(root, '[data-msq-material]').forEach((button) => {
        const value = button.dataset.msqMaterial === 'custom' ? 'PP Material' : button.dataset.msqMaterial === 'standard' ? 'PC Reinforced' : button.dataset.msqMaterial;
        button.classList.toggle('is-active', value === state.material);
      });
      $$(root, '[data-msq-package-toggle]').forEach((button) => {
        const included = button.dataset.msqPackageToggle === 'bag' ? state.standardBag : state.standardBox;
        button.classList.toggle('is-active', included);
        button.setAttribute('aria-pressed', String(included));
      });

      setHidden('[data-msq-advanced]', !advanced);
      setHidden('[data-msq-standard]', advanced);
      setHidden('[data-msq-custom-solid]', !solid);
      setHidden('[data-msq-custom-graphic]', !graphic);
      renderPanels();

      if (summaryImage) {
        const url = colorImage();
        if (url && summaryImage.src !== url) summaryImage.src = url;
      }

      $$(root, '[data-msq-graphic-notes]').forEach((input) => { if (document.activeElement !== input) input.value = state.graphicNotes; });
      $$(root, '[data-msq-market]').forEach((input) => { if (document.activeElement !== input) input.value = state.market; });
      $$(root, '[data-msq-notes]').forEach((input) => { if (document.activeElement !== input) input.value = state.notes; });
      $$(root, '[data-msq-quantity]').forEach((input) => { if (document.activeElement !== input) input.value = String(Math.max(moq, state.quantity)); });

      setText('[data-msq-summary-status]', advanced ? (i18n.advancedVersion || 'ADVANCED OEM CUSTOM') : (i18n.standardVersion || 'STANDARD VERSION'));
      setText('[data-msq-summary="version"]', advanced ? (i18n.advancedVersion || 'ADVANCED OEM CUSTOM') : (i18n.standardVersion || 'STANDARD VERSION'));
      setText('[data-msq-summary="tier"]', advanced ? (i18n.advancedVersion || 'ADVANCED OEM CUSTOM') : (i18n.standardVersion || 'STANDARD VERSION'));
      setText('[data-msq-summary="color"]', displayColorName());
      setText('[data-msq-summary="visor"]', displayVisorName());
      setText('[data-msq-summary="graphicType"]', graphic ? displayValue(state.graphicType) : (i18n.solidColor || 'SOLID COLOR'));
      setText('[data-msq-summary="liner"]', displayValue(state.liner));
      setText('[data-msq-summary="packaging"]', displayValue(state.packaging));
      setText('[data-msq-summary="material"]', displayValue(state.material));
      setText('[data-msq-summary="quantity"]', `${advanced ? Math.max(moq, state.quantity) : moq} ${i18n.pcs || 'PCS'}`);
      setText('[data-msq-summary="market"]', state.market || i18n.willConfirm || 'TO BE CONFIRMED');
      setText('[data-msq-cta-label]', advanced ? (i18n.continueAdvanced || 'CONTINUE WITH ADVANCED OEM CUSTOM') : (i18n.requestStandardQuote || 'REQUEST STANDARD QUOTE'));
      setText('[data-msq-logo-name]', state.logoFile?.name || i18n.chooseLogoFile || 'CHOOSE LOGO FILE');

      syncForm();
      syncModalPreview();
    }

    function openModal() {
      render();
      if (!modal) return;
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('msq-inquiry-open');
      requestAnimationFrame(() => modal.classList.add('is-open'));
      setTimeout(() => modal.querySelector('input:not([type="hidden"]), select, textarea, button')?.focus({ preventScroll: true }), 80);
    }
    function closeModal() {
      if (!modal) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('msq-inquiry-open');
      setTimeout(() => { if (!modal.classList.contains('is-open')) modal.hidden = true; }, 220);
    }

    $$(root, '[data-msq-tier]').forEach((button) => button.addEventListener('click', () => {
      state.tier = button.dataset.msqTier === 'advanced' ? 'advanced' : 'standard';
      if (state.tier === 'advanced' && !state.step) state.step = 'color';
      render();
    }));
    $$(root, '[data-msq-package-toggle]').forEach((button) => button.addEventListener('click', () => {
      if (button.dataset.msqPackageToggle === 'bag') state.standardBag = !state.standardBag;
      if (button.dataset.msqPackageToggle === 'box') state.standardBox = !state.standardBox;
      render();
    }));
    $$(root, '[data-msq-oem-step]').forEach((button) => button.addEventListener('click', () => {
      state.step = button.dataset.msqOemStep || 'color';
      render();
    }));
    $$(root, '[data-msq-custom-type-solid]').forEach((button) => button.addEventListener('click', () => {
      state.customType = 'solid';
      render();
    }));
    $$(root, '[data-msq-custom-type-graphic]').forEach((button) => button.addEventListener('click', () => {
      state.customType = 'graphic';
      render();
    }));
    $$(root, '[data-msq-finish]').forEach((button) => button.addEventListener('click', () => {
      state.finish = text(button.dataset.msqValue) || state.finish;
      state.finish = state.finish.toLowerCase() === 'gloss' ? 'Gloss' : state.finish.toLowerCase() === 'matte' ? 'Matte' : state.finish;
      render();
    }));
    $$(root, '[data-msq-logo-position]').forEach((button) => button.addEventListener('click', () => {
      const value = text(button.dataset.msqValue).toLowerCase();
      const positions = { side: 'Side', front: 'Front', rear: 'Rear', multiple: 'Multiple Positions' };
      if (positions[value]) state.logoPosition = positions[value];
      render();
    }));
    $$(root, '[data-msq-material]').forEach((button) => button.addEventListener('click', () => {
      state.material = button.dataset.msqMaterial === 'custom' ? 'PP Material' : button.dataset.msqMaterial === 'standard' ? 'PC Reinforced' : (button.dataset.msqMaterial || state.material);
      render();
    }));

    const bindAll = (selector, eventName, property, transform = (value) => value) => {
      $$(root, selector).forEach((input) => input.addEventListener(eventName, (event) => {
        state[property] = transform(event.target.value);
        render();
      }));
    };
    bindAll('[data-msq-custom-color]', 'input', 'customColor');
    bindAll('[data-msq-graphic-notes]', 'input', 'graphicNotes');
    bindAll('[data-msq-market]', 'input', 'market');
    bindAll('[data-msq-notes]', 'input', 'notes');
    bindAll('[data-msq-quantity]', 'input', 'quantity', (value) => Math.max(moq, Number(value || moq)));

    $(root, '[data-msq-logo-file]')?.addEventListener('change', (event) => {
      state.logoFile = event.target.files?.[0] || null;
      render();
    });
    modalQuantity?.addEventListener('input', (event) => { state.quantity = Math.max(moq, Number(event.target.value || moq)); });
    modalQuantity?.addEventListener('blur', () => render());

    $(root, '[data-msq-cta]')?.addEventListener('click', openModal);
    form?.addEventListener('submit', () => {
      if (modalQuantity) state.quantity = Math.max(moq, Number(modalQuantity.value || moq));
      syncForm();
    }, true);
    modal?.querySelectorAll('[data-msq-modal-close]').forEach((button) => button.addEventListener('click', closeModal));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && modal?.classList.contains('is-open')) closeModal(); });
    document.addEventListener('click', (event) => {
      const link = event.target.closest?.('a[href="#V12OemRequest"]');
      if (!link) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      openModal();
    }, true);

    render();
    if (modal?.querySelector('[data-msq-modal-feedback]')) openModal();
  }

  const boot = (scope = document) => $$(scope, '[data-ms-quick-oem]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
